import type { InvoiceTemplate } from "@stb/api-contract"
import type { CSSProperties } from "react"

import { formatAbn, type InvoiceDocumentData } from "../../document"
import { invoiceDocumentCss } from "./invoice-document-css"

export interface InvoiceDocumentProps {
  data: InvoiceDocumentData
  /** Unsent draft: adds the DRAFT watermark and the "number is set when you send" note. */
  draft?: boolean
}

/** The paper styles; render once next to a preview. The PDF puts the same CSS in its head. */
export function InvoiceDocumentStyles({ template = "classic" }: { template?: InvoiceTemplate }) {
  return <style>{invoiceDocumentCss(template)}</style>
}

/**
 * THE invoice document: the live preview, the detail page and the PDF all render this one
 * component from `buildInvoiceDocument` data, so they cannot drift (invoicing plan). A seller
 * that is not GST registered has no GST column, row or words in `data`, and so none here.
 */
export function InvoiceDocument({ data, draft = false }: InvoiceDocumentProps) {
  const { seller, buyer, look } = data
  const template = look?.template ?? "classic"
  const style = look
    ? ({
        "--inv-accent": look.accent,
        "--inv-ink": look.ink,
        "--inv-banner-bg": look.bannerBg,
        "--inv-banner-fg": look.bannerFg,
      } as CSSProperties)
    : undefined
  return (
    <article className={`inv-doc inv-t-${template}`} style={style} aria-label="Invoice preview">
      {draft && (
        <div className="inv-watermark" aria-hidden="true">
          DRAFT
        </div>
      )}
      <header className="inv-head">
        <div>
          <h2 className="inv-title">{data.title}</h2>
          <p className="inv-number">{data.number}</p>
        </div>
        <div className="inv-seller">
          {look?.logoDataUri && <img className="inv-logo" src={look.logoDataUri} alt="" />}
          <strong>{seller.name}</strong>
          {seller.abn && <div>ABN {formatAbn(seller.abn)}</div>}
          {seller.addressLines.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
      </header>

      <div className="inv-meta">
        {(buyer.name || buyer.abn) && (
          <div>
            <p className="inv-label">Bill to</p>
            {buyer.name && <p className="inv-value">{buyer.name}</p>}
            {buyer.abn && <p className="inv-value">ABN {formatAbn(buyer.abn)}</p>}
            {buyer.address && <p className="inv-value">{buyer.address}</p>}
          </div>
        )}
        <div>
          <p className="inv-label">Issued</p>
          <p className="inv-value">{data.issueDate}</p>
        </div>
        <div>
          <p className="inv-label">Due</p>
          <p className="inv-value">{data.dueDate}</p>
        </div>
      </div>

      <table className="inv-table">
        <thead>
          <tr>
            <th scope="col">Description</th>
            <th scope="col" className="inv-num">
              Qty
            </th>
            <th scope="col" className="inv-num">
              Unit price
            </th>
            {data.showGst && (
              <th scope="col" className="inv-num">
                GST
              </th>
            )}
            <th scope="col" className="inv-num">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {data.lines.map((line, index) => (
            <tr key={index}>
              <td>{line.description}</td>
              <td className="inv-num">{line.quantity}</td>
              <td className="inv-num">{line.unitAmount}</td>
              {data.showGst && <td className="inv-num">{line.gst}</td>}
              <td className="inv-num">
                {line.amount}
                {line.taxLabel && <span className="inv-tax">{line.taxLabel}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="inv-totals">
        {data.totals.map((row) => (
          <div key={row.label} className={row.label === "Total" ? "inv-total" : undefined}>
            <span>{row.label}</span>
            <span>{row.amount}</span>
          </div>
        ))}
      </div>

      {data.paid && (
        <p className="inv-paid" data-testid="paid-stamp">
          PAID
          <span>
            {data.paid.receivedOn} by {data.paid.method}
          </span>
        </p>
      )}

      {data.bank && (
        <section className="inv-section">
          <p className="inv-label">Pay by bank transfer</p>
          {data.bank.accountName && (
            <p className="inv-value">Account name: {data.bank.accountName}</p>
          )}
          {data.bank.bsb && <p className="inv-value">BSB: {data.bank.bsb}</p>}
          {data.bank.accountNumber && (
            <p className="inv-value">Account number: {data.bank.accountNumber}</p>
          )}
        </section>
      )}
      {data.note && (
        <section className="inv-section">
          <p className="inv-note">{data.note}</p>
        </section>
      )}
      {draft && <p className="inv-draft-note">Number is set when you send.</p>}
    </article>
  )
}
